import type { DestinationContext, TrackingDestination } from '../types'

declare global {
  interface Window {
    dataLayer?: any[]
  }
}

const normalizeEmail = (email: string) => email.trim().toLowerCase()

async function sha256Hex(input: string): Promise<string> {
  if (typeof window === 'undefined' || !window.crypto?.subtle) return ''
  const data = new TextEncoder().encode(input)
  const hash = await window.crypto.subtle.digest('SHA-256', data)
  return Array.from(new Uint8Array(hash))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

export class GoogleAdsDestination implements TrackingDestination {
  public readonly key = 'googleAds' as const
  public readonly requiredConsent = 'marketing' as const

  track(
    eventName: string,
    properties?: Record<string, any>,
    ctx?: DestinationContext
  ) {
    if (typeof window === 'undefined') return
    if (eventName !== 'conversion') return

    const email =
      (properties?.email && typeof properties.email === 'string'
        ? properties.email
        : null) || (ctx?.identity?.email ? String(ctx.identity.email) : null)
    const { email: _email, ...dataLayerProperties } = properties || {}

    const pushConversionEvent = (hashedEmail?: string) => {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'google_ads_conversion',
        ...dataLayerProperties,
        ...(hashedEmail ? { user_data: { email: hashedEmail } } : {}),
      })
    }

    if (email) {
      void (async () => {
        try {
          const hashedEmail = await sha256Hex(normalizeEmail(email))
          if (!hashedEmail) {
            pushConversionEvent()
            return
          }
          pushConversionEvent(hashedEmail)
        } catch {
          pushConversionEvent()
        }
      })()
      return
    }

    pushConversionEvent()
  }
}
